import React, { FC, useEffect, useState } from 'react'
import { Button, GetProp, Image, message, Space, Table, Upload, UploadProps } from 'antd'
import { memory, translate } from '../../../core'
import { InputCard } from '../../inventory/component/InputCard'
import { TokenResponse } from '../../auth/type'
import { DraggableRow, DraggableTable, DragHandle } from '../../../core/component'
import { DeleteOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import { ImageSlider } from '../type/image-slider'
import { AUTHENTICATION_TOKEN } from '../../../App.constants'
import { HttpRequest } from '../../../core/http.service'
import { resolvePathFile } from '../../../core/until'
import { UploadImageResponse } from '../../shared/type'
import Enumerable from 'linq'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

export type ImageSliderListProps = {
  onUpdate: (input: Omit<ImageSlider, 'key'>[]) => void
  images: ImageSlider[]
  loading: boolean
}

export const ImageSliderListBase: FC<ImageSliderListProps> = ({onUpdate, images, loading}) => {
  const [data, setData] = useState<ImageSlider[]>([])
  const token = memory.get<TokenResponse>(AUTHENTICATION_TOKEN)
  const [uploading, setUploading] = useState<boolean>(false)

  useEffect(() => {
    setData(images?.map(it => ({...it, key: it.priority.toString()})) ?? [])
  }, [images])

  const handleUpdate = async (items: ImageSlider[]) => {
    const input = items
      .filter(it => !!it.fileName)
      .sort((a, b) => a.priority - b.priority)
      .map(({fileName}, index) => ({
        fileName,
        priority: index,
      }))

    onUpdate(input)
  }

  const onDragEnd = async ({active, over}: DragEndEvent) => {
    if (active.id === over?.id)
      return

    setData((prevState) => {
      const activeIndex = prevState.findIndex((record) => record.key === active?.id)
      const overIndex = prevState.findIndex((record) => record.key === over?.id)
      const newItems = arrayMove(prevState, activeIndex, overIndex)

      onUpdate(newItems.map(({fileName}, index) => ({
        fileName,
        priority: index,
      })))

      return newItems
    })
  }

  const handleUploadChange: UploadProps['onChange'] = async ({file}) => {
    if (file.status === 'uploading') {
      setUploading(true)
      return
    }
    if (file.status === 'done') {
      setUploading(false)
      const [{filename}] = file.response as UploadImageResponse[]
      const priority = (data.length
        ? Enumerable.from(data)
          .select(it => it.priority)
          .max()
        : -1) + 1

      const items = [
        ...data,
        {
          fileName: filename,
          priority,
          key: priority.toString(),
        } as ImageSlider
      ]

      await handleUpdate(items)
    }
  }

  return (
    <InputCard className='content-size'>
      <Space>
        <Upload
          action={`${new HttpRequest().baseURL}/files/image`}
          name='images'
          headers={{
            Authorization: `${token?.token_type} ${token?.access_token}`
          }}
          showUploadList={false}
          beforeUpload={(file: FileType) => {
            if (file.type.includes('image/'))
              return true

            message.error(translate('upload_format'))
            return false
          }}
          onChange={handleUploadChange}
        >
          <Button
            type='primary'
            icon={uploading ? <LoadingOutlined/> : <PlusOutlined/>}
          >
            {translate('add')}
          </Button>
        </Upload>
      </Space>
      <DraggableTable
        items={data}
        onDragEnd={onDragEnd}
      >
        <Table<ImageSlider>
          rowKey='key'
          className='mt-2'
          loading={loading}
          components={{body: {row: DraggableRow}}}
          columns={[
            {
              key: 'sort',
              align: 'center',
              width: 10,
              render: () => <DragHandle/>
            },
            {
              dataIndex: 'priority',
              title: translate('display', 'order'),
              key: 'priority',
              align: 'center',
              width: 10,
            },
            {
              dataIndex: 'image',
              title: translate('image'),
              width: 100,
              render: (_, {fileName}) =>
                <Image
                  width={200}
                  height={100}
                  src={resolvePathFile(fileName ?? '')}
                />
            },
            {
              dataIndex: 'key',
              key: 'action',
              width: 10,
              render: key => (
                <Button
                  type='text'
                  shape='circle'
                  icon={<DeleteOutlined/>}
                  onClick={() => {
                    setData(prevState => prevState.filter(it => it.key !== key))
                    return handleUpdate(data.filter(it => it.key !== key))
                  }}
                />
              )
            }
          ]}
          dataSource={data}
          pagination={false}
        />
      </DraggableTable>
    </InputCard>
  )
}
